import React, { useState, useEffect, useRef } from 'react';

const Carousel = ({ images = [], videos = [] }) => {
  const items = videos.length > 0 ? videos : images;
  const isVideoCarousel = videos.length > 0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const videoRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const hasPlayedFirstRef = useRef(false);

  const goToNextItem = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  useEffect(() => {
    if (isVideoCarousel || items.length === 0) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        goToNextItem();
        setIsAnimating(false);
      }, 500); // Half second for fade out, then change image
    }, 10000);

    return () => clearInterval(interval);
  }, [isVideoCarousel, items]);

  useEffect(() => {
    if (!isVideoCarousel) return;
    const video = videoRef.current;
    if (!video) return;

    // Solo el primer video arranca muteado para garantizar el autoplay (los
    // navegadores bloquean el autoplay con sonido). A partir del segundo, o si
    // el usuario ya interactuó, se reproduce con audio.
    const shouldMute = !hasInteractedRef.current && !hasPlayedFirstRef.current;
    video.volume = 1;

    const playWith = (muted) => {
      video.muted = muted;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          if (!muted) {
            // Reintenta muteado si el navegador rechazó el autoplay con sonido.
            playWith(true);
          }
        });
      }
    };

    playWith(shouldMute);
  }, [isVideoCarousel, currentIndex, items]);

  // Activa el sonido en la primera interacción del usuario (clic/tecla/touch).
  useEffect(() => {
    if (!isVideoCarousel) return;

    const enableSound = () => {
      hasInteractedRef.current = true;
      const video = videoRef.current;
      if (video) {
        video.muted = false;
        video.volume = 1;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {});
        }
      }
    };

    window.addEventListener('pointerdown', enableSound, { once: true });
    window.addEventListener('keydown', enableSound, { once: true });
    window.addEventListener('touchstart', enableSound, { once: true });

    return () => {
      window.removeEventListener('pointerdown', enableSound);
      window.removeEventListener('keydown', enableSound);
      window.removeEventListener('touchstart', enableSound);
    };
  }, [isVideoCarousel]);

  useEffect(() => {
    if (items.length === 0) return;
    if (currentIndex >= items.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, items.length]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [isVideoCarousel]);

  const handleVideoEnded = () => {
    // A partir de que termina el primer video, el resto ya no arranca muteado.
    hasPlayedFirstRef.current = true;
    setIsAnimating(true);
    setTimeout(() => {
      goToNextItem();
      setIsAnimating(false);
    }, 500);
  };

  return (
    <div className="carousel-container">
      {items.length > 0 && (
        <div className="carousel">
          <div className="carousel-image-container">
            {isVideoCarousel ? (
              <video
                ref={videoRef}
                src={items[currentIndex].src}
                className={`carousel-image ${isAnimating ? 'fade-out' : 'fade-in'}`}
                autoPlay
                controls
                muted
                playsInline
                preload="metadata"
                onEnded={handleVideoEnded}
              />
            ) : (
              <img
                src={items[currentIndex].src}
                alt={items[currentIndex].alt}
                className={`carousel-image ${isAnimating ? 'fade-out' : 'fade-in'}`}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;

